import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { finalize, of as observableOf } from 'rxjs';
import { LanguageService } from '../../language/language.service';
import { DialogService } from '../dialog.service';
import { environment } from 'src/environments/environment';
import * as _ from 'lodash';
import { OtpService } from '../../otp/otp.service';
import { Integer } from 'aws-sdk/clients/apigateway';
 
@Component({
  selector: 'esc-otp-dialog',
  templateUrl: './otp-dialog.component.html',
  styleUrls: ['./otp-dialog.component.scss']
})
export class OtpDialogComponent implements OnInit {
  public otpForm: FormGroup;
  public isConfirming: boolean = false;
  public errorMessage: String;
  public otpCountdown: Integer = 0;
  public requesting: boolean = false;
  public maskedRecipient: string;
  public skippable: boolean;
  public otpType: string;
  public requestForOtp: boolean;

  constructor(
    public dialogRef: MatDialogRef<OtpDialogComponent>,
    private fb: FormBuilder,
    private dialogService: DialogService,
    private languageService: LanguageService,
    private otpService: OtpService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  checkLabelMap: Record<string, string> = {
    mobile: 'phone',
    whatsapp: 'WhatsApp',
    email: 'email'
  };
 
  ngOnInit(): void {
    this.skippable = this.data?.skippable || false;
    const registerPatientSettings = JSON.parse(localStorage.getItem('register_patient_settings'))
    this.requestForOtp = registerPatientSettings.request_for_otp;

    switch (this.data.type) {
      case 'mobile':
        this.otpType = this.languageService.getTranslation(
          'LABEL_MOBILE_NUMBER',
          {}
        );
        break;
      case 'whatsapp':
        this.otpType = this.languageService.getTranslation(
          'LABEL_WHATSAPP_NUMBER',
          {}
        );
        break;
      case 'email':
        this.otpType = this.languageService.getTranslation('LABEL_EMAIL', {});
        break;
    }
    this.initForm();
    this.maskRecipient();
    this.requestOtp();
  }
 
  initForm() {
    this.otpForm = this.fb.group({
      otp1: [null, Validators.required],
      otp2: [null, Validators.required],
      otp3: [null, Validators.required],
      otp4: [null, Validators.required],
      otp5: [null, Validators.required],
      otp6: [null, Validators.required]
    });
  }
 
  maskRecipient() {
    let first: string, mask: string, last: string;
    switch (this.data.type) {
      case 'mobile':
      case 'whatsapp':
        first = this.data.recipient.substring(0, 3);
        mask = this.data.recipient
          .substring(3, this.data.recipient.length - 4)
          .replace(/./g, '*');
        last = this.data.recipient.substring(this.data.recipient.length - 4);
        break;
      case 'email':
        let indexAt = this.data.recipient.indexOf('@');
        first = this.data.recipient.charAt(0);
        mask = this.data.recipient.substring(1, indexAt - 1).replace(/./g, '*');
        last = this.data.recipient.substring(
          indexAt - 1,
          this.data.recipient.length
        );
        break;
    }
    this.maskedRecipient = first + mask + last;
  }
 
  focusNextInput(event: KeyboardEvent, step: number): void {
    const input = event.target as HTMLInputElement;
    const prevElement = document.getElementById(`id_otp_otp${step - 1}`) as HTMLInputElement;
    const currentControl = this.otpForm.get(`otp${step}`);
    const cursorStart = input.selectionStart;
    const cursorEnd = input.selectionEnd;

    // Handle Backspace
    if (event.key === 'Backspace') {
    
      // Handle selection or highlight
      if (cursorStart !== cursorEnd) {
        currentControl.setValue('');
        return;
      }

      if (input.value === '') {
        // Move to previous input if empty
        if (prevElement) {
          prevElement.focus();
          this.otpForm.get(`otp${step - 1}`).setValue(null);
        }
      } else if (cursorStart === 0) {
        // Cursor at start just focus previous input
        if (prevElement) prevElement.focus();
        event.preventDefault();
      } else {
        // Delete current value
        currentControl.setValue(null);
      }
    }
  }
  
  handleBeforeInput(event, step: number): void {
    event.preventDefault();
    const nextElement = document.getElementById(`id_otp_otp${step + 1}`) as HTMLInputElement;
    const currentControl = this.otpForm.get(`otp${step}`);
    const value = event.data
    const isNumber = /^\d$/.test(value);

    if (isNumber) {
      currentControl.setValue(Number(value))
      if (nextElement) {
        nextElement.focus()
      }
    }
  }

  cascadePaste(event) {
    let clipboardData = event.clipboardData;
    let pastedText = clipboardData.getData('text');
    let maxLength = event.target.maxLength;
    let elementIndex =
      pastedText.length / maxLength > maxLength
        ? 6
        : pastedText.length / maxLength; // dev note: hard-coded 6 here represents the last of the text input array
    this.otpForm.patchValue({
      otp1: pastedText.charAt(0),
      otp2: pastedText.charAt(1),
      otp3: pastedText.charAt(2),
      otp4: pastedText.charAt(3),
      otp5: pastedText.charAt(4),
      otp6: pastedText.charAt(5)
    });
    document.getElementById('id_otp_otp' + elementIndex).focus();
  }
 
  requestOtp() {
    if (!this.requestForOtp) {
      this.setOtpResendCountdown();
      return;
    }
 
    if (this.otpCountdown > 0) {
      return;
    }
 
    this.requesting = true;
    this.otpService
      .requestOtp({
        recipient: this.data.recipient,
        type: this.data.type,
        name: this.data.name,
        schema_code: this.data.schema_code
      })
      .pipe(
        finalize(() => {
          this.requesting = false;
        })
      )
      .subscribe(
        (res) => {
          this.setOtpResendCountdown();
        },
        (err) => {
          if (environment.showLogs) {
            console.error(err);
          }
 
          const msg_error =
            _.get(err, 'response.data.code') === 'E(249xOTP001)'
              ? 'MSG_ERROR_OTP_INTERVAL'
              : 'MSG_ERROR_NETWORK_CONNECTION';
 
          this.dialogService.openSnackBar(
            this.languageService.getTranslation(msg_error, {})
          );
          return observableOf(null);
        }
      );
  }
 
  setOtpResendCountdown() {
    this.otpCountdown = 300;
 
    let otpCountdownIntrv = setInterval(() => {
      this.otpCountdown -= 1;
 
      if (this.otpCountdown <= 0) {
        clearInterval(otpCountdownIntrv);
      }
    }, 1000);
  }
 
  validate(value: any) {
    this.isConfirming = true;
    this.otpForm.disable();
    let isValid = true;
 
    try {
      let data = {
        otp:
          String(_.get(value, 'otp1')) +
          String(_.get(value, 'otp2')) +
          String(_.get(value, 'otp3')) +
          String(_.get(value, 'otp4')) +
          String(_.get(value, 'otp5')) +
          String(_.get(value, 'otp6'))
      };
 
      if (data.otp.length < 6) {
        this.errorMessage = this.languageService.getTranslation(
          'MSG_ERROR_REGISTRATION_CODE_LENGTH',
          {}
        );
        isValid = false;
      }
 
      if (isValid) {
        this.onSubmit(data);
      } else {
        this.isConfirming = false;
        this.otpForm.enable();
      }
    } catch (err) {
      console.log(err);
      this.isConfirming = false;
      this.otpForm.enable();
    }
  }
 
  onSubmit(value: any) {
    if (this.data) {
      this.otpService
        .verifyOtp({
          recipient: this.data.recipient,
          type: this.data.type,
          doctor_code: this.data.schema_code,
          ...value
        })
        .pipe(
          finalize(() => {
            this.isConfirming = false;
            this.otpForm.enable();
          })
        )
        .subscribe(
          (res) => {
            if (environment.showLogs) {
              console.log(res);
            }
 
            if (
              res == null ||
              res.status == 0 ||
              res.status == 400 ||
              res.status == 500 ||
              res.status == 502 ||
              res.status == 504
            ) {
              this.dialogService.openSnackBar(
                this.languageService.getTranslation(
                  'MSG_ERROR_NETWORK_CONNECTION',
                  {}
                )
              );
              return;
            }
 
            if (String(_.get(res, 'value')).toLowerCase() === 'success') {
              this.dialogService.openSnackBar(
                this.languageService.getTranslation('MSG_OTP_VERIFIED', {})
              );
              this.dialogRef.close(true);
            } else {
              if (_.get(res, 'code').includes('001')) {
                this.errorMessage = _.get(res, 'value');
                this.otpCountdown = 0;
                this.requesting = false;
              } else {
                this.errorMessage = this.languageService.getTranslation(
                  'MSG_ERROR_INVALID_OTP',
                  {}
                );
              }
            }
          },
          (err) => {
            if (environment.showLogs) {
              console.error(err);
            }
 
            this.errorMessage = this.languageService.getTranslation(
              'MSG_ERROR_NETWORK_CONNECTION',
              {}
            );
            return observableOf(null);
          }
        );
    }
  }
}